import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const useRichTextHeight = (contentRef: any, content: any, dependencies: any[] = []) => {
    const [height, setHeight] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const rafRef = useRef<any>(null);
    const observerRef = useRef<any>(null);
    const timeoutRef = useRef<any>(null);

    const measureHeight = useCallback(() => {
        if (!contentRef.current) return;

        // Cancel previous RAF nếu có
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
            const element = contentRef.current;
            if (element) {
                const newHeight = element.scrollHeight;
                
                // Chỉ update nếu có thay đổi và height > 0
                if (newHeight > 0 && newHeight !== height) {
                    setHeight(newHeight);
                    setIsReady(true);
                }
            }
        });
    }, [height]);

    useLayoutEffect(() => {
        if (!content || !contentRef.current) {
            setIsReady(false);
            return;
        }

        setIsReady(false);

        // Cleanup previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Sử dụng ResizeObserver - hiệu quả nhất cho việc detect size changes
        observerRef.current = new ResizeObserver((entries) => {
            if (entries.length > 0) {
                measureHeight();
            }
        });

        observerRef.current.observe(contentRef.current);

        // Fallback: measure sau khi content thay đổi
        timeoutRef.current = setTimeout(measureHeight, 100);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [content, ...dependencies]);

    // Cleanup khi unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { height, isReady };
};